/**
 * RippleEngine — manages a list of expanding ripple wavefronts and draws them
 * to a single 2D canvas. ONE draw call per frame regardless of dot count.
 *
 * v3 §1 key insight: ripple is a wavefront on the canvas overlay, NOT a
 * per-dot animation. Dots stay in the SVG identity layer; canvas paints the
 * visual wave on top. Cost: ~1ms/frame at any dot count.
 *
 * Per stu_plan--epl-dot-map-component-v3.md.
 */

export interface RippleSpec {
  /** Center in spec coords (matches the SVG viewBox). */
  x: number;
  y: number;
  /** Color to paint the ring. CSS color string. */
  color?: string;
  /** Lifetime in ms. Default 1400. */
  life?: number;
  /** Maximum radius reached at end of life, in spec units. Default 700. */
  maxRadius?: number;
  /** Ring thickness in spec units. Default 24. */
  thickness?: number;
  /** Easing curve identifier. Default 'easeOutCubic'. */
  ease?: 'linear' | 'easeOutCubic' | 'easeOutQuart';
}

interface ActiveRipple extends Required<Omit<RippleSpec, 'color'>> {
  color: string;
  born: number;
  id: number;
}

const EASING = {
  linear: (t: number) => t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeOutQuart: (t: number) => 1 - Math.pow(1 - t, 4),
} as const;

export class RippleEngine {
  private _ripples: ActiveRipple[] = [];
  private _nextId = 0;

  /** Spec coordinate space (matches SVG viewBox). Defaults to 1600×514. */
  specWidth = 1600;
  specHeight = 514;

  /** Add a ripple. Returns the assigned ID for later cancellation. */
  fire(spec: RippleSpec): number {
    const id = ++this._nextId;
    this._ripples.push({
      id,
      x: spec.x,
      y: spec.y,
      color: spec.color ?? 'rgba(0, 102, 255, 0.85)',
      life: spec.life ?? 1400,
      maxRadius: spec.maxRadius ?? 700,
      thickness: spec.thickness ?? 24,
      ease: spec.ease ?? 'easeOutCubic',
      born: performance.now(),
    });
    return id;
  }

  cancel(id: number): void {
    this._ripples = this._ripples.filter((r) => r.id !== id);
  }

  clear(): void {
    this._ripples = [];
  }

  get activeCount(): number {
    return this._ripples.length;
  }

  /**
   * Draw all active ripples to the canvas. Returns true if any are still alive
   * (so the host can decide whether to keep RAF running).
   *
   * Coordinate mapping: spec coords → canvas pixels via fit-contain projection
   * to match how the SVG layer is sized (`preserveAspectRatio="xMidYMid meet"`).
   */
  draw(ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number, t: number): boolean {
    // Clear the entire canvas — ripples are the only thing drawn on it.
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    if (this._ripples.length === 0) return false;

    // fit-contain: scale spec coords by the smaller of width/height ratios,
    // then center the result. Mirrors SVG preserveAspectRatio="xMidYMid meet".
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvasWidth / dpr;
    const cssH = canvasHeight / dpr;
    const scale = Math.min(cssW / this.specWidth, cssH / this.specHeight);
    const offsetX = (cssW - this.specWidth * scale) / 2;
    const offsetY = (cssH - this.specHeight * scale) / 2;

    // Cull dead ripples in place.
    const alive: ActiveRipple[] = [];

    for (const r of this._ripples) {
      const elapsed = t - r.born;
      const p = elapsed / r.life;
      if (p >= 1) continue; // expired
      alive.push(r);

      const eased = EASING[r.ease](p);
      const radiusSpec = r.maxRadius * eased;
      const radiusPx = radiusSpec * scale;
      // Guard: canvas RAF can fire one frame before scale resolves to a non-zero
      // value (e.g. canvas size 1×1 transitional state). Skip until valid.
      if (!Number.isFinite(radiusPx) || radiusPx <= 0 || !Number.isFinite(scale) || scale <= 0) continue;
      const cx = r.x * scale + offsetX;
      const cy = r.y * scale + offsetY;
      const alpha = 1 - p; // linear fade out

      // Outer faint glow ring (wider, lower alpha)
      ctx.beginPath();
      ctx.arc(cx, cy, radiusPx, 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(r.color, alpha * 0.4);
      ctx.lineWidth = Math.max(1, r.thickness * scale);
      ctx.stroke();

      // Inner sharp ring
      ctx.beginPath();
      ctx.arc(cx, cy, radiusPx, 0, Math.PI * 2);
      ctx.strokeStyle = withAlpha(r.color, alpha * 0.85);
      ctx.lineWidth = Math.max(1, r.thickness * scale * 0.4);
      ctx.stroke();
    }

    this._ripples = alive;
    return alive.length > 0;
  }
}

/** Convert any CSS color string into rgba() with custom alpha override.
 *  Cheap implementation: handles rgba/rgb/hex 3 and 6. Falls back to passthrough. */
function withAlpha(color: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\(([^)]+)\)/, (_, parts: string) => {
      const [r, g, b] = parts.split(',').map((s) => s.trim());
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    });
  }
  if (color.startsWith('rgb(')) {
    return color.replace(/rgb\(([^)]+)\)/, (_, parts: string) => {
      return `rgba(${parts}, ${a})`;
    });
  }
  if (color.startsWith('#')) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return color;
}
